import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Images, Maximize2, X } from 'lucide-react';
import { assetUrl } from '../../utils/assetUrl.js';
import { getVacacionImagePaths } from '../../utils/vacacionImages.js';

function GalleryLightbox({ images, active, onClose, onChange }) {
  const safeActive = images.length > 0 ? Math.min(active, images.length - 1) : 0;
  const hasMultiple = images.length > 1;

  const goPrev = useCallback(() => {
    onChange((safeActive - 1 + images.length) % images.length);
  }, [images.length, onChange, safeActive]);

  const goNext = useCallback(() => {
    onChange((safeActive + 1) % images.length);
  }, [images.length, onChange, safeActive]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasMultiple) goPrev();
      if (e.key === 'ArrowRight' && hasMultiple) goNext();
    }
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [goNext, goPrev, hasMultiple, onClose]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex min-h-0 flex-col bg-black"
      style={{ width: '100vw', height: '100dvh', maxHeight: '100dvh' }}
      role="dialog"
      aria-modal="true"
      aria-label="Galería ampliada"
    >
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-4 py-3 text-white">
        <p className="text-sm font-medium text-white/80">
          Foto {safeActive + 1} de {images.length}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
          aria-label="Cerrar galería"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="relative min-h-0 w-full flex-1">
        {hasMultiple ? (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white shadow-lg transition hover:bg-white/25 md:left-5"
            aria-label="Foto anterior"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
        ) : null}

        <div className="absolute inset-0 flex items-center justify-center p-4 md:px-20 md:py-6">
          <img
            src={assetUrl(images[safeActive])}
            alt=""
            className="max-h-full max-w-full object-contain object-center"
            style={{ maxHeight: '100%', maxWidth: '100%' }}
            decoding="async"
          />
        </div>

        {hasMultiple ? (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white shadow-lg transition hover:bg-white/25 md:right-5"
            aria-label="Foto siguiente"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        ) : null}
      </div>

      {hasMultiple ? (
        <ul className="flex shrink-0 gap-2 overflow-x-auto border-t border-white/10 bg-black/80 px-4 py-3">
          {images.map((path, index) => {
            const selected = index === safeActive;
            return (
              <li key={path} className="shrink-0">
                <button
                  type="button"
                  onClick={() => onChange(index)}
                  className={`flex h-16 w-20 items-center justify-center overflow-hidden rounded-lg bg-white p-1 ring-2 ${
                    selected ? 'ring-accent-lime' : 'ring-transparent opacity-70 hover:opacity-100'
                  }`}
                  aria-label={`Ir a foto ${index + 1}`}
                  aria-pressed={selected}
                >
                  <img src={assetUrl(path)} alt="" className="max-h-full max-w-full object-contain" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default function VacacionImageGallery({ sede, className = '' }) {
  const images = useMemo(
    () => getVacacionImagePaths(sede),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evitar recalcular por referencia nueva del objeto sede
    [sede?.id, sede?.imagen, sede?.galeria]
  );
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setActive(0);
    setLightboxOpen(false);
  }, [sede?.id]);

  const safeActive = images.length > 0 ? Math.min(active, images.length - 1) : 0;
  const current = images[safeActive];

  const openLightbox = useCallback(() => setLightboxOpen(true), []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  if (images.length === 0) {
    return (
      <div
        className={`flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-xl bg-white p-6 text-slate-400 ring-1 ring-slate-200 ${className}`}
      >
        <Images className="h-10 w-10 text-accent-lime" aria-hidden />
        <span className="text-sm">Sin fotos publicadas</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="group relative">
        <div className="flex aspect-[4/3] items-center justify-center rounded-xl bg-white p-4 ring-1 ring-slate-200">
          <button
            type="button"
            onClick={openLightbox}
            className="flex h-full w-full cursor-zoom-in items-center justify-center border-0 bg-transparent p-0"
            aria-label="Expandir galería de fotos"
          >
            <img
              src={assetUrl(current)}
              alt=""
              className="mx-auto block max-h-full max-w-full object-contain object-center"
              decoding="async"
            />
          </button>
        </div>
        <button
          type="button"
          onClick={openLightbox}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-brand-navy/90 px-3 py-1.5 text-xs font-semibold text-white shadow-md ring-1 ring-white/20 transition hover:bg-brand-navy group-hover:bg-brand-navy"
          aria-label="Expandir galería"
        >
          <Maximize2 className="h-3.5 w-3.5" aria-hidden />
          Expandir
        </button>
      </div>

      {images.length > 1 ? (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Galería de fotos</p>
            <button
              type="button"
              onClick={openLightbox}
              className="text-xs font-semibold text-accent-lime hover:text-[#0d2b7f]"
            >
              Ver en pantalla completa
            </button>
          </div>
          <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            {images.map((path, index) => {
              const selected = index === safeActive;
              return (
                <li key={path}>
                  <button
                    type="button"
                    onClick={() => setActive(index)}
                    onDoubleClick={openLightbox}
                    className={`relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg bg-white p-1 ring-2 transition ${
                      selected ? 'ring-accent-lime' : 'ring-slate-200 hover:ring-[#1acb12]/50'
                    }`}
                    aria-label={`Ver foto ${index + 1} de ${images.length}`}
                    aria-pressed={selected}
                  >
                    <img src={assetUrl(path)} alt="" className="max-h-full max-w-full object-contain" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {lightboxOpen && typeof document !== 'undefined'
        ? createPortal(
            <GalleryLightbox images={images} active={safeActive} onClose={closeLightbox} onChange={setActive} />,
            document.body
          )
        : null}
    </div>
  );
}
